import {
  CSSProperties,
  type FunctionComponent,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ExpandingButtonProps } from '.';

export interface ExpandingButtonContextType
  extends Required<ExpandingButtonProps> {
  collapseRef: React.MutableRefObject<HTMLDivElement | null>;
  open: boolean;
  onClick: () => void;
  contentStyle: CSSProperties;
  disabled: boolean;
}

export const ExpandingButtonContext =
  createContext<ExpandingButtonContextType | null>(null);

export const useExpandingButtonContext = () =>
  useContext(ExpandingButtonContext) as ExpandingButtonContextType;

export interface ExpandingButtonProviderProps extends ExpandingButtonProps {
  children: ReactNode;
}

const ExpandingButtonProvider: FunctionComponent<ExpandingButtonProps> =
  function ({
    button,
    buttonId,
    content,
    direction = 'down',
    milliseconds = 300,
    children,
  }) {
    const [open, setOpen] = useState<boolean>(false);
    const [disabled, setDisabled] = useState<boolean>(false);
    const [maxHeight, setMaxHeight] = useState<string>('0px');
    const collapseRef = useRef<HTMLDivElement | null>(null);

    const handleDisable = useCallback(() => {
      setDisabled(true);
      setTimeout(() => setDisabled(false), milliseconds);
    }, [milliseconds]);

    const onClick = useCallback(() => {
      const clientHeight = `${collapseRef.current?.clientHeight || 100}px`;
      if (disabled) return;
      handleDisable();
      if (!open) {
        setOpen(true);
        setTimeout(() => {
          setMaxHeight(clientHeight);
        }, 10);
        setTimeout(() => {
          setMaxHeight('none');
        }, milliseconds + 100);
        return;
      }
      setOpen(false);
      setMaxHeight(clientHeight);
      setTimeout(() => setMaxHeight('0px'), 10);
    }, [handleDisable, open, disabled, milliseconds]);

    const contentStyle = useMemo(
      () => ({
        maxHeight,
        transitionDuration: `${milliseconds}ms`,
      }),
      [maxHeight, milliseconds],
    );

    const values = useMemo(
      () => ({
        button,
        buttonId,
        content,
        direction,
        milliseconds,
        collapseRef,
        open,
        onClick,
        contentStyle,
        disabled,
      }),
      [
        button,
        buttonId,
        content,
        direction,
        milliseconds,
        collapseRef,
        open,
        onClick,
        contentStyle,
        disabled,
      ],
    );

    return (
      <ExpandingButtonContext.Provider value={values}>
        {children}
      </ExpandingButtonContext.Provider>
    );
  };

export default ExpandingButtonProvider;
